import { useState } from "react";

// ────────────────────────────────────────────────────────────────────────
// ScreenSpark — Privacy Policy
// Same visual system/pattern as the ChargeSpark / DBLearn / PDF Studio
// policy pages (dark "legal document" layout, Space Grotesk display type).
// Content written for what ScreenSpark actually does: a "display over
// other apps" decorative particle/animation overlay (49+ themes across
// Love/Nature/Weather/Ambient categories), a floating on-screen control
// button, boot-restore of the last active effect, on-device theme/settings
// storage via SharedPreferences, no account, no user file uploads, and
// Google Mobile Ads (AdMob) for monetization.
//
// BEFORE DEPLOYING:
//   1. Confirm CONTACT_EMAIL / DEVELOPER_NAME are correct.
//   2. Re-read every section against what the app actually does. If you
//      add accounts, cloud sync, analytics, or a backend later, this page
//      must be updated to stay accurate — a privacy policy that doesn't
//      match real behavior is a compliance risk, not just a copy problem.
//   3. If you build out a GDPR/UK consent (UMP) flow for AdMob, update the
//      "Advertising" section to describe it — this draft describes AdMob
//      usage but does not claim a specific consent-form implementation
//      unless one exists in the app.
//   4. Swap the AdMob test ad unit IDs in ads_service.dart, and the test
//      APPLICATION_ID in AndroidManifest.xml, for your real ones before
//      release — this policy assumes the real AdMob SDK is live and
//      serving ads.
// ────────────────────────────────────────────────────────────────────────

const CONTACT_EMAIL = "alphay28071994@gmail.com"; // developer + support email
const DEVELOPER_NAME = "AlphaY"; // TODO: replace with your name/company
const APP_NAME = "ScreenSpark";

// Pulled from ScreenSpark's in-app theme (colorSchemeSeed 0xFFFF6B9D, a
// warm pink) so the policy page reads as part of the same product, not a
// generic template.
const palette = {
    bg: "#0A0511",
    surface: "#150C1F",
    raised: "#1D1229",
    border: "#332140",
    accent: "#FF6B9D",
    accentDim: "#2E1522",
    magenta: "#C24CFF",
    magentaDim: "#241033",
    text: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    faint: "rgba(255,255,255,0.4)",
    success: "#22E37A",
    successDim: "#0B2A18",
    danger: "#FF5A5F",
    dangerDim: "#2B0F10",
    warning: "#FFA726",
    warningDim: "#2B1F0A",
    info: "#FF6B9D",
    infoDim: "#2E1522",
    gold: "#FFC24B",
    goldDim: "#2B1F0A",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&family=JetBrains+Mono:wght@500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  .ss-pp {
    background: ${palette.bg};
    color: ${palette.text};
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    line-height: 1.7;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  .ss-bars-bg {
    position: fixed;
    inset: 0;
    background-image: repeating-linear-gradient(
      90deg,
      ${palette.border}33 0px,
      ${palette.border}33 2px,
      transparent 2px,
      transparent 60px
    );
    pointer-events: none;
    z-index: 0;
    opacity: 0.5;
  }

  .ss-glow {
    position: fixed;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    width: 640px;
    height: 420px;
    background: radial-gradient(ellipse, ${palette.accent}22 0%, ${palette.magenta}14 45%, transparent 70%);
    pointer-events: none;
    z-index: 0;
    animation: ss-pulse 5s ease-in-out infinite alternate;
  }

  @keyframes ss-pulse {
    from { opacity: 0.6; }
    to   { opacity: 1; }
  }

  .ss-wrap {
    position: relative;
    z-index: 1;
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px 80px;
  }

  .ss-hero {
    text-align: center;
    padding: 56px 0 44px;
    animation: ss-fadeup .6s ease both;
  }

  .ss-mark {
    width: 52px;
    height: 52px;
    margin: 0 auto 18px;
    border-radius: 14px;
    background: linear-gradient(135deg, ${palette.accent}, ${palette.magenta});
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }

  .ss-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}44;
    border-radius: 100px;
    padding: 5px 14px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: ${palette.accent};
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .ss-hero h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(30px, 6vw, 48px);
    font-weight: 700;
    letter-spacing: -1px;
    line-height: 1.1;
    margin-bottom: 12px;
  }

  .ss-hero h1 em {
    font-style: normal;
    background: linear-gradient(90deg, ${palette.accent}, ${palette.magenta});
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .ss-hero-sub {
    color: ${palette.muted};
    font-size: 14px;
    max-width: 460px;
    margin: 0 auto 18px;
  }

  .ss-meta {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    color: ${palette.faint};
    font-size: 12px;
    font-family: 'JetBrains Mono', monospace;
  }

  .ss-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .ss-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, ${palette.border}, transparent);
    margin: 36px 0;
  }

  .ss-section {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 16px;
    padding: 24px 26px 20px;
    margin-bottom: 12px;
    transition: border-color .2s;
    animation: ss-fadeup .5s ease both;
  }

  .ss-section:hover { border-color: ${palette.accent}30; }

  .ss-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .ss-icon {
    width: 36px; height: 36px; min-width: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .ss-section h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -.1px;
  }

  .ss-section p, .ss-section li {
    color: ${palette.muted};
    font-size: 14px;
    line-height: 1.75;
  }

  .ss-section ul {
    padding-left: 18px;
    margin-top: 8px;
  }

  .ss-section li { margin-bottom: 5px; }

  .ss-section p + p { margin-top: 10px; }

  .ss-section a {
    color: ${palette.info};
    text-decoration: none;
    border-bottom: 1px solid ${palette.info}44;
    transition: border-color .15s;
  }

  .ss-section a:hover { border-color: ${palette.info}; }

  .ss-box {
    border-radius: 10px;
    padding: 11px 15px;
    margin-top: 14px;
    font-size: 13px;
    display: flex;
    gap: 9px;
    align-items: flex-start;
    line-height: 1.6;
  }

  .ss-box-success {
    background: ${palette.successDim};
    border: 1px solid ${palette.success}33;
    color: ${palette.success}CC;
  }

  .ss-box-warning {
    background: ${palette.warningDim};
    border: 1px solid ${palette.warning}33;
    color: ${palette.warning}CC;
  }

  .ss-box-danger {
    background: ${palette.dangerDim};
    border: 1px solid ${palette.danger}33;
    color: ${palette.danger}CC;
  }

  .ss-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 14px 0 4px;
  }

  @media (max-width: 520px) { .ss-grid { grid-template-columns: 1fr; } }

  .ss-card {
    background: ${palette.raised};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 13px 15px;
    display: flex;
    align-items: flex-start;
    gap: 11px;
  }

  .ss-card-icon {
    width: 32px; height: 32px; min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .ss-card strong {
    font-size: 13px;
    font-weight: 600;
    color: ${palette.text};
    display: block;
    margin-bottom: 3px;
  }

  .ss-card p {
    font-size: 12px;
    color: ${palette.muted};
    line-height: 1.5;
    margin: 0 !important;
  }

  .ss-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 14px;
    font-size: 13px;
  }

  .ss-table th, .ss-table td {
    text-align: left;
    padding: 9px 10px;
    border-bottom: 1px solid ${palette.border};
    vertical-align: top;
  }

  .ss-table th {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    letter-spacing: .5px;
    text-transform: uppercase;
    color: ${palette.faint};
    font-weight: 600;
  }

  .ss-table td { color: ${palette.muted}; }
  .ss-table td:first-child { color: ${palette.text}; font-weight: 600; white-space: nowrap; }

  .ss-contact {
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}33;
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    margin-top: 36px;
  }

  .ss-contact h3 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .ss-contact p {
    color: ${palette.muted};
    font-size: 14px;
    margin-bottom: 20px;
  }

  .ss-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(90deg, ${palette.accent}, ${palette.magenta});
    color: #0A0511;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 12px 24px;
    border-radius: 100px;
    text-decoration: none;
    transition: opacity .2s, transform .2s;
  }

  .ss-contact-btn:hover { opacity: .87; transform: translateY(-1px); }

  .ss-footer {
    text-align: center;
    padding-top: 28px;
    color: ${palette.faint};
    font-size: 12px;
  }

  @keyframes ss-fadeup {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const Ico = ({ e }) => <span role="img" aria-hidden="true">{e}</span>;

function SecIcon({ bg, children }) {
    return <div className="ss-icon" style={{ background: bg }}>{children}</div>;
}

function Section({ iconBg, icon, title, children, delay = 0 }) {
    return (
        <div className="ss-section" style={{ animationDelay: `${delay}s` }}>
            <div className="ss-section-head">
                <SecIcon bg={iconBg}><Ico e={icon} /></SecIcon>
                <h2>{title}</h2>
            </div>
            {children}
        </div>
    );
}

function SuccessBox({ children }) {
    return <div className="ss-box ss-box-success"><Ico e="✓" /><span>{children}</span></div>;
}

function WarnBox({ children }) {
    return <div className="ss-box ss-box-warning"><Ico e="⚠" /><span>{children}</span></div>;
}

function InfoCard({ icon, iconBg, title, desc }) {
    return (
        <div className="ss-card">
            <div className="ss-card-icon" style={{ background: iconBg }}>
                <Ico e={icon} />
            </div>
            <div>
                <strong>{title}</strong>
                <p>{desc}</p>
            </div>
        </div>
    );
}

export default function ScreenSparkPrivacyPolicy() {
    const [now] = useState(() => new Date());
    const year = now.getFullYear();
    const month = now.toLocaleString("en-US", { month: "long" });

    return (
        <>
            <style>{css}</style>
            <div className="ss-pp">
                <div className="ss-bars-bg" />
                <div className="ss-glow" />

                <div className="ss-wrap">

                    <header className="ss-hero">
                        <div className="ss-mark">✨</div>
                        <div className="ss-badge">🔐 &nbsp;Legal document</div>
                        <h1>Privacy <em>Policy</em></h1>
                        <p className="ss-hero-sub">
                            How <strong>{APP_NAME}</strong> collects, uses, and protects your information.
                        </p>
                        <div className="ss-meta">
                            <span>🏢 Developer: {DEVELOPER_NAME}</span>
                            <span>📅 Effective: {month} {year}</span>
                            <span>📱 No account · No file uploads</span>
                        </div>
                    </header>

                    <div className="ss-divider" />

                    <Section iconBg={palette.accentDim} icon="✨" title="Overview" delay={0.05}>
                        <p>
                            <strong style={{ color: palette.text }}>{APP_NAME}</strong> is a screen-personalization app
                            for Android. It draws animated particle effects and decorative overlays — falling hearts,
                            cherry blossoms, snowfall, fireflies, and more — on top of your screen, using Android's
                            "Display over other apps" permission, along with a floating shortcut button to pause or stop
                            the effect at any time.
                        </p>
                        <p>
                            This page explains what information (if any) {APP_NAME} collects, how the app uses device
                            permissions to make the overlay work, and how advertising is handled. By using {APP_NAME},
                            you agree to the practices described below.
                        </p>
                        <SuccessBox>
                            {APP_NAME} requires no account or sign-in, and does not collect your name, email, or any
                            other personal identifying information.
                        </SuccessBox>
                    </Section>

                    <Section iconBg={palette.infoDim} icon="🗂️" title="Information we collect" delay={0.10}>
                        <p>
                            <strong style={{ color: palette.text }}>We do not operate any servers that this app sends your
                                data to.</strong> There is no account system and no crash-reporting or analytics service built
                            by us into this app.
                        </p>
                        <p>
                            Everything {APP_NAME} remembers about your setup — the effect theme you last applied, whether
                            the overlay is currently on, your per-theme settings (density, speed, color, size, and other
                            toggles), your favorited themes, and whether the floating shortcut button is shown — is stored
                            locally on your device (via Android SharedPreferences), inside the app's own private storage.
                            None of it is transmitted to us or to any server we control.
                        </p>
                        <p>
                            A small copy of this same "which effect is active" state is also kept on the device natively,
                            so that if your phone restarts, the last active effect can resume automatically without
                            needing the Flutter app to be opened first. This copy never leaves your device either.
                        </p>
                        <SuccessBox>
                            Uninstalling the app, or clearing its storage in your device settings, permanently deletes
                            all locally saved theme selections, settings, favorites, and preferences.
                        </SuccessBox>
                    </Section>

                    <Section iconBg={palette.warningDim} icon="📢" title="Advertising" delay={0.15}>
                        <p>
                            {APP_NAME} is free to use and supported by ads served through{" "}
                            <strong style={{ color: palette.text }}>Google Mobile Ads (AdMob)</strong>. The app may show a
                            banner ad while you browse themes, an app-open ad on cold start, an interstitial ad after you
                            close a theme preview without applying it, and a rewarded ad at certain points where watching
                            one is optional.
                        </p>
                        <p>
                            To serve ads, Google's SDK may collect device and advertising identifiers (such as your
                            Android advertising ID), approximate location derived from IP address, and other technical
                            data, and may use these to personalize the ads you see. This processing is performed by
                            Google, not by {APP_NAME} directly — we don't receive or store this data ourselves.
                        </p>
                        <p>
                            Where applicable law requires consent for personalized advertising (for example, in the EEA,
                            UK, and Switzerland), ad personalization is subject to Google's own consent-management
                            requirements for its Mobile Ads SDK.
                        </p>
                        <p>
                            Watching or interacting with any ad is never required to use {APP_NAME}'s core features —
                            browsing, favoriting, and applying overlay themes all work whether or not an ad is shown or
                            completed.
                        </p>
                        <WarnBox>
                            Google's ad handling is governed by Google's own privacy policy —{" "}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                                policies.google.com/privacy
                            </a>{" "}
                            and{" "}
                            <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noreferrer">
                                policies.google.com/technologies/ads
                            </a>
                        </WarnBox>
                    </Section>

                    <Section iconBg={palette.successDim} icon="🔑" title="Permissions used" delay={0.20}>
                        <p>
                            {APP_NAME} requests the following Android permissions, all of them used solely to make the
                            screen overlay and its controls work — none are used to collect personal data:
                        </p>
                        <table className="ss-table">
                            <thead>
                                <tr><th>Permission</th><th>Why it's needed</th></tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Display over other apps</td>
                                    <td>Draws the animated effect overlay and the floating shortcut button on top of your home screen and other apps. This is the core mechanism the app is built around.</td>
                                </tr>
                                <tr>
                                    <td>Foreground service</td>
                                    <td>Runs a persistent, always-visible-notification service that keeps the chosen effect rendering in the background for as long as you leave it on.</td>
                                </tr>
                                <tr>
                                    <td>Notifications</td>
                                    <td>Required by Android to keep the foreground service running; shows a persistent low-priority notification while an effect is active.</td>
                                </tr>
                                <tr>
                                    <td>Run at device startup</td>
                                    <td>Restarts your last active effect automatically after you reboot your device, so the overlay keeps working without having to reopen the app.</td>
                                </tr>
                                <tr>
                                    <td>Ignore battery optimizations (optional)</td>
                                    <td>An optional exemption you can grant so aggressive OEM battery managers (MIUI, ColorOS, EMUI, etc.) don't kill the overlay service in the background. The app works without it, just less reliably on some devices.</td>
                                </tr>
                                <tr>
                                    <td>Internet &amp; advertising ID</td>
                                    <td>Used only to request, load, and display ads through Google Mobile Ads (see Advertising above).</td>
                                </tr>
                            </tbody>
                        </table>
                        <p>
                            {APP_NAME} does not request camera, microphone, contacts, SMS, call log, precise location, or
                            broad storage/file-access permissions, and does not read or write files outside of its own app
                            storage.
                        </p>
                    </Section>

                    <Section iconBg="#150A1F" icon="🔌" title="Third-party components" delay={0.25}>
                        <p>{APP_NAME} is built using the following components:</p>
                        <div className="ss-grid">
                            <InfoCard
                                icon="📢" iconBg={palette.warningDim}
                                title="Google Mobile Ads (AdMob)"
                                desc="Serves banner, app-open, interstitial, and rewarded ads; see Advertising above."
                            />
                            <InfoCard
                                icon="🎨" iconBg={palette.accentDim}
                                title="Bundled effect assets"
                                desc="Icons, images, and animation frames for each theme ship inside the app and are rendered entirely on-device."
                            />
                            <InfoCard
                                icon="🗄️" iconBg={palette.successDim}
                                title="Android SharedPreferences"
                                desc="Stores your selected theme, settings, and favorites on-device."
                            />
                            <InfoCard
                                icon="🖥️" iconBg={palette.infoDim}
                                title="System overlay service"
                                desc="A native Android foreground service renders the chosen effect and the floating control button."
                            />
                            <InfoCard
                                icon="🔁" iconBg={palette.goldDim}
                                title="Boot receiver"
                                desc="Restores your last active effect after a device restart, using an on-device copy of your choice."
                            />
                            <InfoCard
                                icon="⏱️" iconBg="#1A100A"
                                title="Pause scheduler"
                                desc="Lets you pause an active effect for a chosen time window, entirely on-device via Android's alarm system."
                            />
                        </div>
                    </Section>

                    <Section iconBg="#150A1F" icon="🕓" title="Data retention" delay={0.30}>
                        <p>
                            Your selected theme, effect settings, favorites, and preferences remain in the app's local
                            storage until you change them yourself, reset the app's data, or uninstall it — at which point
                            the operating system removes them automatically.
                        </p>
                        <p>We do not maintain any server-side copy of this data.</p>
                    </Section>

                    <Section iconBg={palette.dangerDim} icon="🧒" title="Children's privacy" delay={0.35}>
                        <p>
                            {APP_NAME} is a general-audience personalization app and is not directed at children under 13.
                            Its effect library (Love, Nature, Weather, and Ambient themes — hearts, flowers, snowfall,
                            fireflies, and similar decorative animations) does not contain adult, romantic, or suggestive
                            imagery.
                        </p>
                        <p>
                            We do not knowingly collect personal information from children. Ads shown in the app are
                            served under Google's standard (non-child-directed) treatment.
                        </p>
                        <p>
                            If you believe a child has used the app in a way that concerns you, please contact us at{" "}
                            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                        </p>
                    </Section>

                    <Section iconBg={palette.successDim} icon="🛡️" title="Security" delay={0.40}>
                        <p>
                            Your theme selections, settings, and favorites live in {APP_NAME}'s private, sandboxed storage
                            on your device, inaccessible to other apps.
                        </p>
                        <p>
                            Since nothing is transmitted to us, the biggest safeguard is your own device security — keep
                            it locked with a passcode or biometric lock, and keep the app updated to receive any security
                            fixes.
                        </p>
                    </Section>

                    <Section iconBg={palette.warningDim} icon="🔄" title="Changes to this policy" delay={0.45}>
                        <p>
                            We may update this privacy policy from time to time. Changes will be posted on this page with
                            an updated effective date at the top.
                        </p>
                        <p>
                            Continued use of {APP_NAME} after changes are posted constitutes acceptance of the updated
                            policy.
                        </p>
                    </Section>

                    <Section iconBg={palette.successDim} icon="✅" title="Your consent" delay={0.50}>
                        <p>
                            By using {APP_NAME}, you consent to this privacy policy. If you do not agree, please
                            discontinue use of the app.
                        </p>
                    </Section>

                    <div className="ss-contact">
                        <h3>Questions or concerns?</h3>
                        <p>
                            If you have any questions about how {APP_NAME} handles your data, don't hesitate to get in
                            touch.
                        </p>
                        <a href={`mailto:${CONTACT_EMAIL}`} className="ss-contact-btn">
                            ✉️ &nbsp;{CONTACT_EMAIL}
                        </a>
                    </div>

                    <div className="ss-divider" />
                    <footer className="ss-footer">
                        <p>© {year} <strong style={{ color: palette.muted }}>{DEVELOPER_NAME}</strong> · {APP_NAME} · All rights reserved.</p>
                        <p style={{ marginTop: 5 }}>This app is not affiliated with or endorsed by Google LLC.</p>
                    </footer>

                </div>
            </div>
        </>
    );
}